/**
 * LESSON: SSR data fetching with Vuex
 *
 * In beauty-box, the server injected apiData at build time via a Handlebars
 * template: <script id="apiData1_index">{ ... }</script>
 *
 * In Vue SSR the equivalent pattern is:
 *   1. Server renders the page → component's serverPrefetch() fires
 *   2. serverPrefetch dispatches fetchApiData → commits to store
 *   3. Framework serializes store.state → injects window.__INITIAL_STATE__
 *   4. Client hydrates the store from __INITIAL_STATE__ — no second fetch
 *
 * This gives you the same result as beauty-box (data available on first paint,
 * no client-side loading flash) but in a reactive, component-friendly way.
 */

//const API_URL = 'https://assets.mcomnyapps.net/gift-card-rewards/2026-06/feeds/page_index_data.json';
const API_URL = process.env.NODE_ENV === 'development'
? 'http://localhost:4012/feeds/page_index_data.json'
: 'https://assets.mcomnyapps.net/gift-card-rewards/2026-06/feeds/page_index_data.json';

export default {
  state() {
    return {
      /**
       * null = not yet fetched (initial state before serverPrefetch runs).
       * Components guard against this with v-if checks via the getters below.
       */
      apiData: null,
      serverMessage: null,
    };
  },

  getters: {
    /**
     * Optional chaining (?.) means each getter returns undefined (falsy) when
     * apiData is null. Components use v-if="heroData" to wait for real data.
     */
    serverMessage:        (state) => state.serverMessage,
    heroData:             (state) => state.apiData?.section_heroes,
    offerData:            (state) => state.apiData?.section_offers,
    customSectionsData:   (state) => state.apiData?.custom_sections
  },

  mutations: {
    SET_API_DATA(state, data) {
      state.apiData = data;
    },
    SET_SERVER_MESSAGE(state, message) {
      state.serverMessage = message;
    },
  },

  actions: {
    /**
     * LESSON: Actions for async work
     *
     * Mutations must be synchronous. Any async operation (fetch, setTimeout, etc.)
     * must happen in an action, which then commits the result via a mutation.
     *
     * The `if (state.apiData) return` guard prevents refetching when the store
     * is already hydrated from window.__INITIAL_STATE__ on the client.
     */
    async fetchServerMessage({ commit, state }) {
      if (state.serverMessage) return;
      // Simulating a server-side fetch — in production this would be a real API call
      commit('SET_SERVER_MESSAGE', `Hello from the server! Rendered at: ${new Date().toUTCString()}`);
    },

    async fetchApiData({ commit, state }) {
      if (state.apiData) return;

      try {
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        commit('SET_API_DATA', data);
      } catch (err) {
        console.error('[store] fetchApiData failed:', err);
      }
    },
  },
};
